// The market page's summary under the yard list (securspace-ui#3056): built in the
// browser from the yards the list shows, so it always agrees with the list and the
// prerender snapshot carries it as real text.

export interface SummaryYard {
  city?: string | null;
  pricePerDay?: number | null;   // cents, as the API sends them
  pricePerMonth?: number | null; // cents
}

export interface PriceRange { min: number; max: number } // whole dollars

export interface YardSummary {
  count: number;
  daily?: PriceRange;
  monthly?: PriceRange;
  /** Each town once, most yards first, ties in list order. */
  towns: string[];
}

const MAX_TOWNS = 6;

// Under $1 is a placeholder, not a price (test yards list $0.01); it would read "from $0".
const MIN_PRICE_CENTS = 100;

const range = (cents: Array<number | null | undefined>): PriceRange | undefined => {
  const dollars = cents.filter((c): c is number => typeof c === 'number' && c >= MIN_PRICE_CENTS).map((c) => Math.round(c / 100));
  return dollars.length ? { min: Math.min(...dollars), max: Math.max(...dollars) } : undefined;
};

export const summarizeYards = (yards: SummaryYard[]): YardSummary => {
  const towns = new Map<string, { name: string; count: number; first: number }>();
  yards.forEach((y, i) => {
    const name = (y.city || '').trim();
    if (!name) return;
    const key = name.toLowerCase();
    const t = towns.get(key);
    if (t) t.count += 1;
    // First spelling wins, title-cased if it came in lower case (" compton ").
    else towns.set(key, { name: name === name.toLowerCase() ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : name, count: 1, first: i });
  });
  return {
    count: yards.length,
    daily: range(yards.map((y) => y.pricePerDay)),
    monthly: range(yards.map((y) => y.pricePerMonth)),
    towns: [...towns.values()].sort((a, b) => b.count - a.count || a.first - b.first).map((t) => t.name),
  };
};

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;
const span = (r: PriceRange, unit: string) => (r.min === r.max ? `${usd(r.min)} a ${unit}` : `${usd(r.min)} to ${usd(r.max)} a ${unit}`);

const list = (items: string[]) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** "12 yards near Los Angeles, CA, from $18 to $35 a day or $320 to $1,200 a month, in Commerce, Compton and 4 other towns." */
export const describeYards = (market: { city: string; state: string }, s: YardSummary): string => {
  if (!s.count) return '';
  const prices = [s.daily && span(s.daily, 'day'), s.monthly && span(s.monthly, 'month')].filter(Boolean).join(' or ');
  const shown = s.towns.slice(0, MAX_TOWNS);
  const rest = s.towns.length - shown.length;
  const towns = rest > 0 ? `${shown.join(', ')} and ${rest} other town${rest === 1 ? '' : 's'}` : list(shown);
  return `${s.count} yard${s.count === 1 ? '' : 's'} near ${market.city}, ${market.state}`
    + (prices ? `, from ${prices}` : '')
    + (towns ? `, in ${towns}` : '')
    + '.';
};
